import { Direction2D, LineSegment2D, Point2D } from '@paragon/geometry-library';
import { roundToIncrement } from '@paragon/utilities';

import { findAxisSnapTargetByKey, PreComputedAxisSnapTargets } from './collectAxisSnapTargets';
import {
  ENTITY_SOFT_LOCK,
  FOOT_GRID_INCREMENT_INCHES,
  FOOT_GRID_SNAP_RADIUS,
  MAX_LENGTH_SNAP_DISPLACEMENT,
  MIN_DIRECTION_COMPONENT,
} from './config';
import { getAxisValue, resolveAxisSnapTarget, roundPoint2D } from './helpers';
import { InteractiveEditSnappingSettings } from './settings';
import { resolveSoftLockedCandidate } from './softLocking';
import { AxisSnapTarget, AxisSnapTargetKey, LengthSoftLockState, SNAP_AXES, SnapAxis } from './types';
import {
  chooseHigherPriorityCandidate,
  getVertexCandidateWeight,
  RankedVertexCandidate,
} from './vertexCandidateRanking';

/**
 * Snaps the given segment's end point during an interactive length edit. The base point is treated as a fixed anchor;
 *   the end point is the moving endpoint that gets snapped.
 *
 * Takes and returns an updated `LengthSoftLockState` - its memory for sticky snapping behaviors.
 */
export function snapSegmentLengthDuringInteractiveEdit(
  segment: LineSegment2D,
  snapIncrement: number,
  snapTargets: PreComputedAxisSnapTargets,
  softLockState: LengthSoftLockState,
  settings: InteractiveEditSnappingSettings,
): { point: Point2D; softLockState: LengthSoftLockState } {
  const search = new LengthSnapSearch(segment, snapTargets, softLockState, settings);
  const candidate = search.findBestCandidate();

  // if no good vertex snapping candidates are found, fall back to basic length snapping
  if (candidate == null) {
    const snappedDistance = roundToIncrement(segment.length, snapIncrement);
    const snappedEndpoint = segment.basePoint.translate(segment.direction.toVector(snappedDistance));

    return { point: snappedEndpoint, softLockState: null };
  }

  const snappedPoint = roundPoint2D(segment.endPoint.translate(segment.direction.toVector(candidate.displacement)));

  return {
    point: snappedPoint,
    softLockState: candidate.family === 'entity' ? candidate.targetKey : null,
  };
}

class LengthSnapSearch {
  readonly endPoint: Point2D;
  readonly direction: Direction2D;

  constructor(
    segment: LineSegment2D,
    private readonly axisSnapTargets: PreComputedAxisSnapTargets,
    private readonly softLockState: LengthSoftLockState,
    private readonly settings: InteractiveEditSnappingSettings,
  ) {
    this.endPoint = segment.endPoint;
    this.direction = segment.direction;
  }

  findBestCandidate(): LengthVertexCandidate | null {
    const entityCandidate = this.findBestEntityCandidate();
    const gridCandidate = this.findBestGridCandidate(MAX_LENGTH_SNAP_DISPLACEMENT);

    return chooseHigherPriorityCandidate(entityCandidate, gridCandidate);
  }

  private findBestEntityCandidate(): LengthVertexCandidate | null {
    if (this.axisSnapTargets.x.length === 0 && this.axisSnapTargets.y.length === 0) return null;

    const previousTarget = this.softLockState;
    const bestCandidate = this.findBestEntityLengthCandidate(MAX_LENGTH_SNAP_DISPLACEMENT);
    const lockedCandidate =
      previousTarget != null
        ? this.findLockedEntityLengthCandidate(previousTarget, MAX_LENGTH_SNAP_DISPLACEMENT)
        : null;

    return resolveSoftLockedCandidate(
      lockedCandidate,
      bestCandidate,
      (a, b) => a.targetKey === b.targetKey,
      previousTarget != null,
      ENTITY_SOFT_LOCK,
    );
  }

  private findBestEntityLengthCandidate(maxSnapDisplacement: number): LengthVertexCandidate | null {
    let best: LengthVertexCandidate | null = null;

    for (const axis of SNAP_AXES) {
      for (const target of this.axisSnapTargets[axis]) {
        best = chooseHigherPriorityCandidate(
          best,
          tryCreateEntityLengthCandidate(this.endPoint, this.direction, target, maxSnapDisplacement),
        );
      }
    }

    return best;
  }

  private findLockedEntityLengthCandidate(
    targetKey: AxisSnapTargetKey,
    maxSnapDisplacement: number,
  ): LengthVertexCandidate | null {
    const target = findAxisSnapTargetByKey(this.axisSnapTargets, targetKey);
    if (target == null) return null;

    return tryCreateEntityLengthCandidate(this.endPoint, this.direction, target, maxSnapDisplacement);
  }

  private findBestGridCandidate(maxSnapDisplacement: number): LengthVertexCandidate | null {
    if (this.settings.footGrid === false) {
      return null;
    }

    let best: LengthVertexCandidate | null = null;

    for (const axis of SNAP_AXES) {
      best = chooseHigherPriorityCandidate(
        best,
        tryCreateGridLengthCandidate(this.endPoint, this.direction, axis, maxSnapDisplacement),
      );
    }

    return best;
  }
}

interface LengthVertexCandidate extends RankedVertexCandidate {
  /** The displacement along the segment direction that is needed to snap the endpoint to the target. */
  readonly displacement: number;

  /** Used to identify this target for lookup later (during a different snapping run). */
  readonly targetKey: AxisSnapTargetKey | null;
}

function tryCreateEntityLengthCandidate(
  endPoint: Point2D,
  direction: Direction2D,
  target: AxisSnapTarget,
  maxSnapDisplacement: number,
): LengthVertexCandidate | null {
  const axis = target.axis;
  const directionComponent = getAxisValue(direction, axis);
  if (Math.abs(directionComponent) <= MIN_DIRECTION_COMPONENT) return null;

  const rawAxisValue = getAxisValue(endPoint, axis);
  const distance = Math.abs(target.value - rawAxisValue);
  const displacement = (target.value - rawAxisValue) / directionComponent;
  if (Math.abs(displacement) >= maxSnapDisplacement) return null;

  const snappedEndPoint = endPoint.translate(direction.toVector(displacement));
  const resolvedTarget = resolveAxisSnapTarget(target, snappedEndPoint);
  if (resolvedTarget === null) return null;

  const weight = getVertexCandidateWeight(endPoint.distanceTo(resolvedTarget.snapLocation));

  return {
    family: 'entity',
    displacement,
    distance,
    weight,
    targetKey: target.key,
  };
}

function tryCreateGridLengthCandidate(
  endPoint: Point2D,
  direction: Direction2D,
  axis: SnapAxis,
  maxSnapDisplacement: number,
): LengthVertexCandidate | null {
  const directionComponent = getAxisValue(direction, axis);
  if (Math.abs(directionComponent) <= MIN_DIRECTION_COMPONENT) return null;

  const rawAxisValue = getAxisValue(endPoint, axis);
  const axisTargetValue = roundToIncrement(rawAxisValue, FOOT_GRID_INCREMENT_INCHES);
  const distance = Math.abs(axisTargetValue - rawAxisValue);
  if (distance > FOOT_GRID_SNAP_RADIUS) return null;

  const displacement = (axisTargetValue - rawAxisValue) / directionComponent;
  if (Math.abs(displacement) >= maxSnapDisplacement) return null;

  return {
    family: 'grid',
    displacement,
    distance,
    weight: getVertexCandidateWeight(distance),
    targetKey: null,
  };
}
